"use client";
import { useAppContext } from "@/providers/AppProvider";
import { AnimatePresence, motion } from "framer-motion";
import {
    BookOpen,
    Bookmark,
    ChevronDown,
    FileText,
    Folders,
    Gamepad2,
    Mail,
    MoreHorizontal,
    Trophy,
    User,
} from "lucide-react";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";

const navContainer = {
    hidden: {},
    visible: {
        transition: {
            staggerChildren: 0.08,
        },
    },
};

const navItem = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 },
};

export default function Main({ children }: { children: React.ReactNode }) {
    const pathname = usePathname();
    const mainContainerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const isFirstRender = useRef(true);
    const { mode } = useAppContext();
    const [isMoreOpen, setIsMoreOpen] = useState(false);
    const [visibleCount, setVisibleCount] = useState<number>(5);

    const allNavItems = [
        {
            label: "About",
            path: "/",
            icon: User,
        },
        ...(mode === "tech"
            ? [
                  {
                      label: "Resume",
                      path: "/resume",
                      icon: FileText,
                  },
                  {
                      label: "Case Studies",
                      path: "/case-studies",
                      icon: BookOpen,
                  },
                  {
                      label: "Projects",
                      path: "/projects",
                      icon: Folders,
                  },
              ]
            : []),
        {
            label: "Highlights",
            path: "/achievements",
            icon: Trophy,
        },
        {
            label: "Resources",
            path: "/resources",
            icon: Bookmark,
        },
        {
            label: "Games",
            path: "/games",
            icon: Gamepad2,
        },
        {
            label: "Contact",
            path: "/contact",
            icon: Mail,
        },
    ];

    // Dynamic width calculation for desktop text labels based on main section's actual width
    useEffect(() => {
        const updateVisibleItems = () => {
            const isMobile =
                typeof window !== "undefined" &&
                (window.innerWidth < 768 ||
                    window.matchMedia("(max-width: 767px)").matches);

            if (isMobile) {
                // On mobile (< 768px), show 5 main icons + More icon (Games & Contact inside menu)
                setVisibleCount(5);
            } else {
                // On desktop/tablet (>= 768px), calculate based on text label container width
                const containerWidth =
                    mainContainerRef.current?.clientWidth || window.innerWidth;
                if (containerWidth >= 680) {
                    setVisibleCount(5);
                } else if (containerWidth >= 560) {
                    setVisibleCount(4);
                } else if (containerWidth >= 440) {
                    setVisibleCount(3);
                } else if (containerWidth >= 330) {
                    setVisibleCount(2);
                } else {
                    setVisibleCount(1);
                }
            }
        };

        updateVisibleItems();

        window.addEventListener("resize", updateVisibleItems);
        const mql = window.matchMedia("(max-width: 767px)");
        const handleMql = () => updateVisibleItems();
        mql.addEventListener("change", handleMql);

        if (mainContainerRef.current) {
            const observer = new ResizeObserver(() => updateVisibleItems());
            observer.observe(mainContainerRef.current);
            return () => {
                observer.disconnect();
                window.removeEventListener("resize", updateVisibleItems);
                mql.removeEventListener("change", handleMql);
            };
        }

        return () => {
            window.removeEventListener("resize", updateVisibleItems);
            mql.removeEventListener("change", handleMql);
        };
    }, []);

    // Split items dynamically based on calculated visible count
    const mainNavItems = allNavItems.slice(0, visibleCount);
    const dropdownNavItems = allNavItems.slice(visibleCount);

    const isDropdownActive = dropdownNavItems.some(
        (item) => item.path === pathname,
    );

    // Close dropdown on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsMoreOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const scrollToMain = useCallback(() => {
        if (typeof window !== "undefined") {
            const isMobile =
                window.innerWidth < 768 ||
                window.matchMedia("(max-width: 767px)").matches;

            if (isMobile) {
                if (mainContainerRef.current) {
                    mainContainerRef.current.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                    });
                }
            } else {
                if (mainContainerRef.current) {
                    mainContainerRef.current.scrollTo({
                        top: 0,
                        behavior: "smooth",
                    });
                }
            }
        }
    }, []);

    // Close dropdown and scroll to main on mobile when pathname changes
    useEffect(() => {
        setIsMoreOpen(false);
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        const timer = setTimeout(() => {
            scrollToMain();
        }, 50);

        return () => clearTimeout(timer);
    }, [pathname, scrollToMain]);

    const getLinkStyle = (path: string) => {
        return pathname === path
            ? "text-primary font-medium"
            : "text-text-color hover:text-primary";
    };

    return (
        <main className="relative flex-1 md:h-full md:min-w-0">
            {/* Page Content */}
            <div
                ref={mainContainerRef}
                className="card p-2 pb-20 sm:p-4 sm:pb-20 md:h-full md:overflow-y-auto md:pb-4 md:pt-14 lg:pt-4"
            >
                {children}
            </div>
            {/* Navigation Bar */}
            <motion.div
                variants={navContainer}
                initial="hidden"
                animate="visible"
                className="fixed bottom-0 left-0 right-0 z-40 flex w-full items-center justify-around rounded-none border-r border-t border-border-color bg-background-color px-3 py-3 shadow-lg md:absolute md:bottom-auto md:left-auto md:right-0 md:top-0 md:w-auto md:justify-end md:space-x-6 md:rounded-bl-2xl md:rounded-tr-2xl md:border-b md:border-l md:px-6 md:py-3.5 md:shadow-none"
            >
                {mainNavItems.map(({ label, path, icon: Icon }) => (
                    <motion.span
                        key={path + mode}
                        variants={navItem}
                        transition={{ type: "spring", stiffness: 300 }}
                        className="inline-block"
                    >
                        <Link
                            href={path}
                            scroll={false}
                            onClick={scrollToMain}
                            className={getLinkStyle(path)}
                            aria-label={label}
                        >
                            <span className="hidden md:inline">{label}</span>
                            <span className="inline md:hidden">
                                <Icon className="size-6" />
                            </span>
                        </Link>
                    </motion.span>
                ))}
                {/* Dropdown Menu (More) */}
                {dropdownNavItems.length > 0 && (
                    <div ref={dropdownRef} className="relative inline-block">
                        <motion.button
                            variants={navItem}
                            onClick={() => setIsMoreOpen(!isMoreOpen)}
                            className={`flex items-center gap-1 transition ${
                                isDropdownActive || isMoreOpen
                                    ? "font-medium text-primary"
                                    : "text-text-color hover:text-primary"
                            }`}
                            aria-label="More options"
                        >
                            <span className="hidden items-center gap-1 md:flex">
                                More{" "}
                                <ChevronDown
                                    className={`h-4 w-4 transition-transform duration-200 ${
                                        isMoreOpen ? "rotate-180" : ""
                                    }`}
                                />
                            </span>
                            <span className="inline md:hidden">
                                <MoreHorizontal className="size-6" />
                            </span>
                        </motion.button>
                        {/* Animated Dropdown Menu */}
                        <AnimatePresence>
                            {isMoreOpen && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                    transition={{ duration: 0.15 }}
                                    className="absolute bottom-14 right-2 z-50 min-w-[160px] space-y-1 rounded-2xl border border-border-color bg-background-color p-2 shadow-2xl md:bottom-auto md:right-0 md:top-10"
                                >
                                    {dropdownNavItems.map(
                                        ({ label, path, icon: Icon }) => (
                                            <Link
                                                key={path}
                                                href={path}
                                                scroll={false}
                                                onClick={() => {
                                                    setIsMoreOpen(false);
                                                    scrollToMain();
                                                }}
                                                className={`flex items-center gap-2.5 rounded-xl px-3 py-2 text-xs font-semibold transition ${
                                                    pathname === path
                                                        ? "border border-primary bg-primary/15 text-primary shadow-sm"
                                                        : "text-text-color hover:bg-primary/10 hover:text-primary"
                                                }`}
                                            >
                                                <Icon className="h-4 w-4" />
                                                <span>{label}</span>
                                            </Link>
                                        ),
                                    )}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                )}
            </motion.div>
        </main>
    );
}
